import { useRouter } from "next/router";
import PropTypes from "prop-types";
import { capitalizeFirstLetter } from "@/utils/formatters";
import FooterSection from "@/components/FooterSection";
import HeaderSection from "@/components/HeaderSection";
import PreviewBanner from "@/components/PreviewBanner";
import CustomHead from "@/components/customHead";
import WorldWideMenu from "./WorldWideMenu";
import PromoBanner from "./PromoBanner";
import { useState, useEffect, useRef } from "react";

export default function Layout({
  children,
  headerNavigationData,
  footerNavigationData,
  promoBannerData,
  seoMetadata,
  world,
}) {
  const router = useRouter();
  const footerPrimaryNav = footerNavigationData?.items?.find(
    nav =>
      nav?.metadata?.tags?.[0]?.sys?.id === process.env.NEXT_PUBLIC_SITE_TAG
  )?.fields?.primaryNav;
  const headerNav = headerNavigationData?.items?.find(
    nav =>
      nav?.fields?.internalTitle ===
      `${capitalizeFirstLetter(process.env.NEXT_PUBLIC_COUNTRY_NAME)} - Header Sections`
  )?.fields;
  const logo = headerNav?.logo?.fields?.mediaItem?.fields?.asset?.fields;
  const copyRightLinks =
    footerNavigationData?.items?.[0]?.fields?.copyrightLinks;
  const chatUrl = footerNavigationData?.items?.[0]?.fields?.chatUrl;
  const scripts = headerNav?.scripts;
  const showChat = headerNav?.showChatIcon ?? true;

  const [isScrolled, setIsScrolled] = useState(false);
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const [isWorldHovered, setIsWorldHovered] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const headerLeaveTimer = useRef(null);
  const worldLeaveTimer = useRef(null);

  const visible = !isScrolled || isHeaderHovered || isWorldHovered;

  useEffect(() => {
    globalThis.dispatchEvent(
      new CustomEvent("kohler-header-visible", { detail: { visible } })
    );
  }, [visible]);

  const makeHoverHandler = (setter, timerRef) => value => {
    clearTimeout(timerRef.current);
    if (value) setter(true);
    else timerRef.current = setTimeout(() => setter(false), 100);
  };

  useEffect(() => {
    const onScroll = () => {
      const scrolled = window.scrollY > 50;
      setIsScrolled(scrolled);
      if (scrolled) {
        clearTimeout(headerLeaveTimer.current);
        clearTimeout(worldLeaveTimer.current);
        setIsHeaderHovered(false);
        setIsWorldHovered(false);
      }
    };
    const onResize = () => setIsDesktop(window.innerWidth > 1023);
    onResize();
    window.addEventListener("scroll", onScroll);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <>
      <CustomHead seoMetaData={seoMetadata} scripts={scripts} />
      {router?.isPreview && <PreviewBanner />}
      <WorldWideMenu
        world={world}
        visible={visible}
        setIsHovered={makeHoverHandler(setIsWorldHovered, worldLeaveTimer)}
      />
      {promoBannerData?.items?.length ? (
        <PromoBanner promoBannerData={promoBannerData} />
      ) : null}
      <HeaderSection
        navData={headerNav}
        logo={logo}
        world={world}
        setIsHovered={makeHoverHandler(setIsHeaderHovered, headerLeaveTimer)}
        visible={visible}
      />
      <main
        style={{
          paddingTop: isDesktop && !isScrolled ? "32px" : "0px",
          // transition: "padding-top 0.2s ease",
        }}
      >
        {children}
      </main>
      <FooterSection
        navData={footerPrimaryNav}
        copyRightLinks={copyRightLinks}
        key={router?.locale}
        chatUrl={chatUrl}
        showChat={showChat}
      />
    </>
  );
}

Layout.propTypes = {
  children: PropTypes.node,
  headerNavigationData: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.object),
  }),
  footerNavigationData: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.object),
  }),
  promoBannerData: PropTypes.object,
  seoMetadata: PropTypes.object,
  world: PropTypes.object,
};
